import React from 'react';
import { Link } from 'react-router';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { Points } from 'components/Points/Points';
import { LeaderboardEntry, LeaderboardCategory, CATEGORY_CONFIG } from 'types/interfaces/leaderboard';
import { entrySubLine, formatDisplayValue, isPointsCategory } from '../../utils/leaderboardDisplay';
import './LeaderboardTable.css';

interface LeaderboardTableProps {
  /** Page 1: podium then rankings, concatenated by the page. */
  rows: LeaderboardEntry[];
  category: LeaderboardCategory;
  isLoading?: boolean;
  currentUserId?: number | null;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  isMobile?: boolean;
}

const MEDALS: Record<number, string> = { 1: 'gold', 2: 'silver', 3: 'bronze' };

const entryUserId = (entry: LeaderboardEntry): number => entry.userId ?? entry.id;

/** Stable row key. A biggest_trade row is a trade; trade.id is a key only, never a URL. */
const rowKey = (entry: LeaderboardEntry): string =>
  entry.trade ? `trade-${entry.trade.id}` : `user-${entryUserId(entry)}`;

/**
 * One table for every board, matching design screen 2a:
 *   - Ranks 1-3: medal circle, 48px avatar, username with the sub-line
 *     directly under it, and the value at the right, large, followed by the
 *     muted stat unit ("20 trades").
 *   - Ranks 4+: #N, avatar, username, the sub-line as its own muted column
 *     right of centre, then the value right-aligned in violet with no unit.
 * There is no tier column: the tier shows only in the Your standing header.
 * Points boards render the value through the Points component.
 */
const LeaderboardTable: React.FC<LeaderboardTableProps> = ({
  rows,
  category,
  isLoading,
  currentUserId,
  onLoadMore,
  hasMore,
  loadingMore,
  isMobile = false,
}) => {
  if (isLoading) {
    return (
      <div className="leaderboard-table">
        <div className="leaderboard-table__loading">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="leaderboard-table__skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="leaderboard-table leaderboard-table--empty">
        <p>No traders found for this board.</p>
      </div>
    );
  }

  const statLabel = CATEGORY_CONFIG[category].statLabel;
  const points = isPointsCategory(category);

  const renderValue = (entry: LeaderboardEntry, isTop: boolean) => {
    if (points) return <Points points={entry.displayValue} type={isTop ? 'default' : 'small'} />;
    const number = (
      <span className="leaderboard-table__value-number">{formatDisplayValue(category, entry.displayValue)}</span>
    );
    return isTop ? (
      <>
        {number}
        <span className="leaderboard-table__value-unit">{statLabel}</span>
      </>
    ) : (
      number
    );
  };

  return (
    <div className="leaderboard-table">
      <div className="leaderboard-table__body">
        {rows.map((entry) => {
          const isTop = entry.rank <= 3;
          const avatarSize = isTop ? (isMobile ? 40 : 48) : 36;
          const isMe = currentUserId != null && entryUserId(entry) === currentUserId;
          const subLine = entrySubLine(entry, category);
          const partner = entry.trade?.partner;
          const className = `leaderboard-table__row${
            isTop ? ` leaderboard-table__row--top leaderboard-table__row--${MEDALS[entry.rank]}` : ''
          }${isMe ? ' leaderboard-table__row--current-user' : ''}${partner ? ' leaderboard-table__row--trade' : ''}`;

          const avatars = (
            <div className="leaderboard-table__avatars">
              <AvatarWithFlag
                avatarUrl={entry.avatarUrl}
                username={entry.username}
                size={avatarSize}
                countryCode={entry.country}
                isElite={entry.isElite}
                showAnimatedBorder={entry.showAnimatedBorder}
                borderColor1={entry.borderColor1}
                borderColor2={entry.borderColor2}
              />
              {partner && (
                <AvatarWithFlag
                  avatarUrl={partner.avatarUrl}
                  username={partner.username}
                  size={avatarSize}
                  countryCode={partner.country}
                  isElite={partner.isElite}
                />
              )}
            </div>
          );

          // A trade belongs to two traders, so each name links on its own and
          // the row itself is not a link (no nested anchors).
          const names = partner ? (
            <span className="leaderboard-table__username">
              <UsernameLink username={entry.username} isElite={entry.isElite} />
              {' & '}
              <UsernameLink username={partner.username} isElite={partner.isElite} />
            </span>
          ) : (
            <UsernameLink
              as="span"
              username={entry.username}
              isElite={entry.isElite}
              className="leaderboard-table__username"
            />
          );

          const cells = (
            <>
              <div className="leaderboard-table__rank">
                {isTop ? (
                  <span className={`leaderboard-table__medal leaderboard-table__medal--${MEDALS[entry.rank]}`}>
                    {entry.rank}
                  </span>
                ) : (
                  <span className="leaderboard-table__rank-number">#{entry.rank}</span>
                )}
              </div>
              <div className="leaderboard-table__member">
                {avatars}
                <div className="leaderboard-table__names">
                  {names}
                  {/* Top rows (and every row on mobile) stack the sub-line under the name. */}
                  {(isTop || isMobile) && <span className="leaderboard-table__subline">{subLine}</span>}
                </div>
              </div>
              {!isTop && !isMobile && <div className="leaderboard-table__subline-col">{subLine}</div>}
              <div className={`leaderboard-table__value${isTop ? ' leaderboard-table__value--top' : ''}`}>
                {renderValue(entry, isTop)}
              </div>
            </>
          );

          return partner ? (
            <div key={rowKey(entry)} className={className}>
              {cells}
            </div>
          ) : (
            <Link key={rowKey(entry)} to={`/profile/u/${encodeURIComponent(entry.username)}`} className={className}>
              {cells}
            </Link>
          );
        })}
      </div>

      {hasMore && (
        <div className="leaderboard-table__load-more">
          <button
            type="button"
            className="leaderboard-table__load-more-btn"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeaderboardTable;
